import React, { useContext } from 'react';
import { useParams } from 'react-router-dom';
import { navigate } from 'gatsby';
import { MasterPage } from 'src/pages-all/master-page/master.page';
import { useDispatch, useSelector } from 'react-redux';
import { useSearchParams } from 'logic/hooks/use-search-params';
import { offerSlice } from 'logic/store/offer/offer.slice';
import { getOfferSearchResults } from 'logic/store/offer/offer.selectors';
import { useInProgress } from 'logic/store/tracker/tracker.hooks';
import { ResultsSearchForm } from 'components/search/results-search/results-search';
import { SearchFormValues, determineNoIndexForSearchPage } from 'components/search/search.logic';
import { OFFERS_PAGE_SIZE } from 'logic/store/offer/search-offer/search-offer.const';
import { TutoringKind } from 'components/search/search.const';
import { Seo } from 'components/seo/seo';
import { translate, translationKeys } from 'logic/translations/translations.service';
import { parseNumberOrDefault } from 'logic/utils/number';
import { AppRoute } from 'app/app.route.const';
import { SearchResultsLocalTutorsPage } from './SearchResultsLocalTutors.page';
import { useDecodedSubjectFromURI } from 'src/pages-all/search/hooks/useDecodedSubjectFromURI';
import { FAQ_CONTENTFUL, getFAQ } from 'logic/faq-meta/faq-meta';
import { CmsContext } from 'logic/context/cms';
import { SortOffersBy } from 'logic/api-models/sort-offer-by';
import { useSearchForm } from '../components/use-search-form';
import { SearchResultsBottomAd } from 'components/ad/ad';
import { AdProvider } from 'components/ad/ad-provider';
import { ExitIntentModal } from 'components/ExitIntentModal/ExitIntentModal';

export default () => {
  const { city } = useParams<{
    city: string | undefined;
    page: string | undefined;
  }>();
  const searchParams = useSearchParams();
  const sortBy = searchParams.get<SortOffersBy>('sortBy');
  const cityId = searchParams.get<string>('cityId');
  const requestTeachingLevel = parseInt(searchParams.get<string>('requestTeachingLevel'));
  const subject = useDecodedSubjectFromURI();
  const page = parseNumberOrDefault(1)(searchParams.get('page'));
  const dispatch = useDispatch();
  const offers = useSelector(getOfferSearchResults);
  const inProgress = useInProgress(offerSlice.actions.searchOffers);
  const { cityToDisplay, subjectToDisplay } = useSearchForm();
  const { faq } = useContext<any>(CmsContext);

  if (!subject && !city) {
    navigate(AppRoute.Home);
  }

  React.useEffect(() => {
    const formValues: SearchFormValues = {
      subjectSlug: subject,
      citySlug: city,
      cityId,
      kind: TutoringKind.onSiteTutoring,
      page,
      size: OFFERS_PAGE_SIZE,
      sortBy,
      requestTeachingLevel,
    };

    dispatch(offerSlice.actions.searchOffers({ formValues }));
  }, [dispatch, subject, city, cityId, page, sortBy, requestTeachingLevel]);

  const noIndex = determineNoIndexForSearchPage(offers, page, subject);

  const seoRoute =
    city && subject
      ? AppRoute.SearchLocalTutorsPerCityAndSubject
      : city
      ? AppRoute.SearchLocalTutorsPerCity
      : AppRoute.SearchLocalTutorsBySubject;

  return (
    <MasterPage inProgress={inProgress}>
      <ExitIntentModal />
      <Seo
        preventScaling
        title={translate(translationKeys.seoRoute[seoRoute].title, {
          city: cityToDisplay,
          subject: subjectToDisplay,
        })}
        faq={getFAQ(faq, FAQ_CONTENTFUL.city, cityToDisplay)}
        noIndex={noIndex}
        description={translate(translationKeys.seoRoute[seoRoute].description, {
          city: cityToDisplay,
          subject: subjectToDisplay,
          availableTutors: offers?.totalCount || 0,
        })}
      />
      <AdProvider>
        <ResultsSearchForm />
        <SearchResultsLocalTutorsPage
          city={cityToDisplay}
          citySlug={city}
          subject={subjectToDisplay}
          subjectSlug={subject}
          offers={offers}
        />
        <SearchResultsBottomAd />
      </AdProvider>
    </MasterPage>
  );
};
